// SMS an einen Kontakt des Leads schreiben und per Klick senden. Stufe 0: Versand über Close (Aktivität mit Status
// "outbox", Close verschickt von der Nummer des Nutzers); später Twilio. Der Weg steht in den Einstellungen.
// Bei Leads ohne bisherigen Kontakt (Werbe-SMS ohne Einwilligung) zeigt der Komposer einen Hinweis und verlangt ein Häkchen.
// Seit Runde 6 (6D): „Später senden“ plant die SMS (nur speichern, Versand erst nach Freigabe, 8 bis 21 Uhr, siehe `smsPlanen.tsx`).
import { useEffect, useMemo, useRef, useState } from "react";
import { ApiFehler } from "../api";
import { smsEingangApi } from "../api/smsEingang";
import { FehlerBox } from "../components/Fehler";
import { Icon } from "../components/Icon";
import { useMeldung } from "../components/Meldungen";
import { entwurfLesen, entwurfSetzen } from "../daten/entwuerfe";
import { useSlashMenue } from "../textbausteine/SlashMenue";
import { useTextbausteine } from "../textbausteine/useTextbausteine";
import { modTaste } from "../werkzeuge/tasten";
import { komm } from "./api";
import { GeplanteSms, planFehler, planIso, planText, SpaeterWahl, standardPlanZeit, type PlanZeit } from "./smsPlanen";
import { kontaktNummern, neueAnfrageId, nichtGsmZeichen, nummerTypText, SMS_HOECHSTENS, smsZaehlen } from "./logik";
import { KomposerKopf, sendenGesperrt, useVorlagen, VorlagenWahl, Warnungen, warnungenMischen } from "./Teile";
import type { KommunikationsStatus, KomposerProps, Pruefung, SmsVorschau, Warnung } from "./typen";
import "./kommunikation.css";
import "./sms.css";

interface Entwurf {
  nummer: string;
  text: string;
}

/** Nach dem Senden über Close: Status nachladen, bis die SMS "sent" oder "error" ist (läuft auch nach dem Schließen weiter). */
function smsBeobachten(activityId: string, meldung: ReturnType<typeof useMeldung>): void {
  let versuch = 0;
  const pruefen = async () => {
    versuch += 1;
    try {
      const s = await komm.smsStand(activityId);
      if (s.status === "sent") return meldung.ok("SMS ist raus.");
      if (s.status === "error") return meldung.zeige("fehler", `Die SMS wurde nicht zugestellt: ${s.error_message ?? "Close meldet einen Fehler."}`, undefined, 15000);
    } catch {
      /* später noch einmal */
    }
    if (versuch < 8) window.setTimeout(() => void pruefen(), 2500);
  };
  window.setTimeout(() => void pruefen(), 2000);
}

export function SmsKomposer({ leadId, kontakte, onClose, onGesendet, kontakt }: KomposerProps) {
  const meldung = useMeldung();
  const schluessel = `${leadId}|sms`;
  const nummern = useMemo(() => kontaktNummern(kontakte), [kontakte]);
  const [entwurf] = useState(() => entwurfLesen<Entwurf>(schluessel));

  const [status, setStatus] = useState<KommunikationsStatus | null>(null);
  const [pruefung, setPruefung] = useState<Pruefung | null>(null);
  const [ladeFehler, setLadeFehler] = useState<unknown>(null);

  const [nummer, setNummer] = useState(() => {
    // Klick aufs SMS-Symbol eines Kontakts: dessen Nummer (Mobil zuerst, siehe kontaktNummern) schlägt den Entwurf
    const gewuenscht = kontakt ? nummern.find((n) => n.kontaktId === kontakt) : undefined;
    if (gewuenscht) return `${gewuenscht.kontaktId}|${gewuenscht.nummer}`;
    return entwurf && nummern.some((n) => `${n.kontaktId}|${n.nummer}` === entwurf.nummer) ? entwurf.nummer : nummern[0] ? `${nummern[0].kontaktId}|${nummern[0].nummer}` : "";
  });
  const [text, setText] = useState(entwurf?.text ?? "");
  const [vorlageId, setVorlageId] = useState("");
  const [vorschau, setVorschau] = useState<SmsVorschau | null>(null);
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<unknown>(null);
  const [bestaetigt, setBestaetigt] = useState<string[]>([]);
  const [zusatz, setZusatz] = useState<Warnung[]>([]);
  const [spaeter, setSpaeter] = useState(false);
  const [planZeit, setPlanZeit] = useState<PlanZeit>(() => standardPlanZeit());
  const [geplantZaehler, setGeplantZaehler] = useState(0);

  const anfrageId = useRef(neueAnfrageId());
  const sendetGerade = useRef(false); // schützt vor zwei Aufrufen im selben Augenblick (Tastenkürzel plus Klick)
  const rahmen = useRef<HTMLDivElement>(null);
  const feld = useRef<HTMLTextAreaElement>(null);
  const vorlagen = useVorlagen("sms");
  const slash = useSlashMenue(useTextbausteine("sms"), text, setText, feld); // "/"-Menü mit Textbausteinen (Agent 8D)

  useEffect(() => {
    let abgebrochen = false;
    Promise.all([komm.status(), komm.pruefung(leadId, "sms")])
      .then(([s, p]) => {
        if (abgebrochen) return;
        setStatus(s);
        setPruefung(p);
      })
      .catch((e) => !abgebrochen && setLadeFehler(e));
    return () => {
      abgebrochen = true;
    };
  }, [leadId]);

  useEffect(() => {
    rahmen.current?.scrollIntoView({ block: "nearest" });
    feld.current?.focus();
  }, []);

  useEffect(() => {
    entwurfSetzen(schluessel, text.trim() ? ({ nummer, text } satisfies Entwurf) : undefined);
  }, [schluessel, nummer, text]);

  const gewaehlt = nummern.find((n) => `${n.kontaktId}|${n.nummer}` === nummer) ?? null;
  const sms = status?.sms ?? null;
  const zaehler = smsZaehlen(vorschau ? vorschau.text : text);
  const warnungen = warnungenMischen(pruefung?.warnungen ?? [], zusatz);
  const { gesperrt, offen } = sendenGesperrt(warnungen, bestaetigt);

  const grund = ((): string | null => {
    if (!status) return "Wird geladen …";
    if (!status.sms.bereit) return status.sms.meldung ?? "SMS-Versand ist nicht eingerichtet.";
    if (gesperrt) return "An diesen Lead wird nicht gesendet.";
    if (!gewaehlt) return "Bitte eine Handynummer auswählen.";
    if (!text.trim()) return "Bitte einen Text schreiben.";
    if (text.length > SMS_HOECHSTENS) return `Zu lang (höchstens ${SMS_HOECHSTENS.toLocaleString("de-DE")} Zeichen).`;
    if (offen.length > 0) return "Bitte den Hinweis oben bestätigen.";
    if (spaeter) return planFehler(planZeit);
    return null;
  })();

  const fussHinweis =
    grund && !laeuft && grund !== "Bitte einen Text schreiben." ? grund : `${modTaste} Enter ${spaeter ? "plant" : "sendet"} · Esc schließt`;

  const vorlageWaehlen = async (id: string) => {
    setVorlageId(id);
    if (!id || !gewaehlt) return;
    setFehler(null);
    try {
      const v = await komm.smsVorschau({ lead_id: leadId, contact_id: gewaehlt.kontaktId, vorlage_id: id });
      setText(v.text);
      setVorschau(null);
      setBestaetigt((b) => b.filter((c) => c !== "platzhalter_leer"));
    } catch (e) {
      setFehler(e);
    }
  };

  const vorschauZeigen = async () => {
    if (!gewaehlt) return;
    setFehler(null);
    try {
      setVorschau(await komm.smsVorschau({ lead_id: leadId, contact_id: gewaehlt.kontaktId, text }));
    } catch (e) {
      setFehler(e);
    }
  };

  const senden = async () => {
    if (grund || laeuft || sendetGerade.current || !gewaehlt) return;
    sendetGerade.current = true;
    setFehler(null);
    setLaeuft(true);
    try {
      const r = await komm.smsSenden({
        lead_id: leadId,
        contact_id: gewaehlt.kontaktId,
        telefon: gewaehlt.nummer,
        text,
        bestaetigt,
        anfrage_id: anfrageId.current,
      });
      entwurfSetzen(schluessel, undefined);
      if (r.sms.weg === "close" && r.sms.status !== "sent") {
        meldung.ok(`SMS an ${gewaehlt.kontaktName} ist bei Close in der Warteschlange.`);
        smsBeobachten(r.activity.id, meldung);
      } else {
        meldung.ok(`SMS an ${gewaehlt.kontaktName} gesendet.`);
      }
      if (!r.close.geloggt) {
        meldung.zeige(
          "info",
          `Die SMS ist raus, steht aber nur im CRM und nicht in Close: ${r.close.fehler ?? "unbekannter Grund"}`,
          { label: "In Close nachtragen", onClick: () => void komm.nachClose(r.activity.id).then(() => meldung.ok("In Close eingetragen."), (e) => meldung.fehler(e, "Nicht eingetragen")) },
          15000,
        );
      }
      onGesendet?.(r.activity);
      onClose();
    } catch (e) {
      if (e instanceof ApiFehler && e.code === "bestaetigung_noetig") {
        setZusatz((z) => warnungenMischen(z, (e.details.warnungen as Warnung[] | undefined) ?? []));
        rahmen.current?.scrollIntoView({ block: "nearest" });
      } else {
        setFehler(e);
      }
    } finally {
      sendetGerade.current = false;
      setLaeuft(false);
    }
  };

  /** Später senden: nur speichern. Dieselben Hinweise wie beim Senden, dieselbe Bestätigung. */
  const planen = async () => {
    if (grund || laeuft || sendetGerade.current || !gewaehlt) return;
    sendetGerade.current = true;
    setFehler(null);
    setLaeuft(true);
    try {
      await smsEingangApi.planen({
        lead_id: leadId,
        contact_id: gewaehlt.kontaktId,
        an: gewaehlt.nummer,
        text,
        geplant_at: planIso(planZeit),
        vorlage_id: vorlageId || undefined,
        bestaetigt,
      });
      entwurfSetzen(schluessel, undefined);
      meldung.ok(`SMS an ${gewaehlt.kontaktName} geplant für ${planText(planZeit)} Uhr.`, { label: "Alle geplanten SMS", to: "/inbox?reiter=messages&filter=geplant" });
      setGeplantZaehler((n) => n + 1);
      onClose();
    } catch (e) {
      if (e instanceof ApiFehler && e.code === "bestaetigung_noetig") {
        setZusatz((z) => warnungenMischen(z, (e.details.warnungen as Warnung[] | undefined) ?? []));
        rahmen.current?.scrollIntoView({ block: "nearest" });
      } else {
        setFehler(e);
      }
    } finally {
      sendetGerade.current = false;
      setLaeuft(false);
    }
  };

  const tastatur = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      e.preventDefault();
      e.stopPropagation();
      onClose();
    } else if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      void (spaeter ? planen() : senden());
    }
  };

  const hatPlatzhalter = text.includes("{{");
  const sonderzeichen = zaehler.kodierung === "UCS-2" ? nichtGsmZeichen(vorschau ? vorschau.text : text) : [];

  return (
    <div ref={rahmen} className="km-karte" onKeyDown={tastatur} role="region" aria-label="SMS schreiben">
      <KomposerKopf
        icon="sms"
        titel="SMS"
        onClose={onClose}
        hinweis={sms ? (sms.absender ? `über ${sms.weg === "close" ? "Close" : "Twilio"} von ${sms.absender}` : "keine Absendernummer") : undefined}
      />

      {ladeFehler ? <FehlerBox fehler={ladeFehler} kompakt /> : null}
      {sms && !sms.bereit ? (
        <div className="hinweis hinweis--warnung">
          <Icon name="warnung" groesse={15} />
          <span>{sms.meldung ?? "SMS-Versand ist nicht eingerichtet."}</span>
        </div>
      ) : null}

      <Warnungen warnungen={warnungen} belege={pruefung?.belege} bestaetigt={bestaetigt} onBestaetigt={setBestaetigt} />

      {nummern.length === 0 ? (
        <div className="hinweis hinweis--warnung">
          <Icon name="warnung" groesse={15} />
          <span>Kein Kontakt dieses Leads hat eine Telefonnummer.</span>
        </div>
      ) : (
        <div className="km-zeile">
          <label className="km-zeile__label" htmlFor="km-nummer">
            An
          </label>
          <select id="km-nummer" className="eingabe eingabe--klein km-nummer" value={nummer} onChange={(e) => {
              setNummer(e.target.value);
              setVorschau(null);
            }}
          >
            {nummern.map((n) => (
              <option key={`${n.kontaktId}|${n.nummer}`} value={`${n.kontaktId}|${n.nummer}`}>
                {n.kontaktName} · {n.anzeige}
                {n.typ ? ` (${nummerTypText(n.typ)})` : ""}
              </option>
            ))}
          </select>
          <VorlagenWahl art="sms" liste={vorlagen.liste} laedt={vorlagen.laedt} wert={vorlageId} onWahl={(id) => void vorlageWaehlen(id)} />
        </div>
      )}

      {vorschau ? (
        <div className="km-vorschau">
          <div className="km-sms-blase">{vorschau.text || "(leer)"}</div>
          {vorschau.fehlende.length > 0 ? (
            <div className="hinweis hinweis--warnung">
              <Icon name="warnung" groesse={15} />
              <span>Diese Angaben fehlen beim Lead und bleiben leer: {vorschau.fehlende.join(", ")}</span>
            </div>
          ) : null}
        </div>
      ) : (
        <div className="bt-slash-rahmen">
          <textarea
            ref={feld}
            className="eingabe eingabe--text km-sms-text"
            rows={4}
            value={text}
            placeholder="Nachricht schreiben … (mit / Textbausteine einfügen)"
            aria-label="SMS-Text"
            {...slash.feldProps(setText)}
          />
          {slash.menue}
        </div>
      )}

      {hatPlatzhalter || vorschau ? (
        <div className="km-zeile km-zeile--leise">
          <button type="button" className="km-link" onClick={() => (vorschau ? setVorschau(null) : void vorschauZeigen())}>
            {vorschau ? "Text bearbeiten" : "Vorschau mit Platzhaltern"}
          </button>
        </div>
      ) : null}

      {sonderzeichen.length > 0 ? (
        <div className="km-sonderzeichen" role="note">
          {sonderzeichen.length === 1 ? "Das Zeichen" : "Die Zeichen"} {sonderzeichen.slice(0, 5).map((z) => `„${z}“`).join(", ")} {sonderzeichen.length === 1 ? "steht" : "stehen"} nicht im GSM-Alphabet. Darum passen nur {zaehler.je_segment} Zeichen in einen SMS-Teil, die Nachricht wird in mehr Teile geteilt und kostet entsprechend mehr.
        </div>
      ) : null}

      {fehler ? <FehlerBox fehler={fehler} kompakt /> : null}

      {spaeter ? <SpaeterWahl wert={planZeit} onChange={setPlanZeit} /> : null}

      <div className="km-fuss">
        {spaeter ? (
          <button type="button" className="knopf knopf--primaer" disabled={Boolean(grund) || laeuft} onClick={() => void planen()} title={grund ?? `Planen (${modTaste} Enter)`}>
            {laeuft ? "Speichert …" : "Planen"}
          </button>
        ) : (
          <button type="button" className="knopf knopf--primaer" disabled={Boolean(grund) || laeuft} onClick={() => void senden()} title={grund ?? `Senden (${modTaste} Enter)`}>
            {laeuft ? "Sendet …" : "Senden"}
          </button>
        )}
        <button
          type="button"
          className="knopf"
          aria-pressed={spaeter}
          title={spaeter ? "Doch sofort senden" : "SMS später senden: planen statt sofort senden"}
          onClick={() => setSpaeter((s) => !s)}
        >
          <Icon name="uhr" groesse={13} /> {spaeter ? "Sofort senden" : "Später senden"}
        </button>
        {/* Leeres Textfeld: kein „Bitte einen Text schreiben“, der Zähler 0 / 1.000 sagt es schon (wie in Close) */}
        <span className="km-fuss__hinweis" title={fussHinweis}>{fussHinweis}</span>
        <span
          className={`km-zaehler${text.length > SMS_HOECHSTENS ? " km-zaehler--zu-lang" : ""}`}
          aria-live="polite"
          title={`${zaehler.zeichen.toLocaleString("de-DE")} von höchstens ${SMS_HOECHSTENS.toLocaleString("de-DE")} Zeichen · ${zaehler.segmente} ${zaehler.segmente === 1 ? "SMS" : "SMS-Teile"} (${zaehler.kodierung}, ${zaehler.je_segment} Zeichen je Teil)`}
        >
          {zaehler.zeichen.toLocaleString("de-DE")} / {SMS_HOECHSTENS.toLocaleString("de-DE")}
          {zaehler.segmente > 1 ? ` · ${zaehler.segmente} SMS` : ""}
        </span>
        <button
          type="button"
          className="symbolknopf km-fuss__weg"
          aria-label="Verwerfen"
          title="Text verwerfen und schließen"
          onClick={() => {
            entwurfSetzen(schluessel, undefined);
            onClose();
          }}
        >
          <Icon name="papierkorb" groesse={15} />
        </button>
      </div>

      <GeplanteSms leadId={leadId} neuLaden={geplantZaehler} />
    </div>
  );
}
